/**
 * BillingModal - Rich modal for billing flows
 *
 * Replaces CrossPlatformAlert for billing dialogs (cancel retention,
 * interval change, plan change, checkout confirmation). Renders the
 * same component on all platforms for visual consistency.
 *
 * Features:
 * - GlassCard container with chrome border
 * - Vertically stacked buttons (fixes horizontal overflow)
 * - 4 button variants with visual hierarchy
 * - Spring entrance animation (react-native-reanimated)
 * - Backdrop tap to close (unless preventBackdropClose)
 * - Optional icon, title, description, feature list
 * - testID support for E2E
 *
 * Task: #1498
 */
import React, { useEffect } from 'react';
import {
  View,
  Text,
  Pressable,
  Modal,
  StyleSheet,
  ScrollView,
  useWindowDimensions,
  AccessibilityInfo,
  Platform,
} from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from 'react-native-reanimated';
import { GlassCard } from '../glass';
import { ANIMATION } from '../../lib/design-tokens';
import { BillingModalButtonComponent } from './BillingModalButton';
import type { BillingModalConfig } from './types';

interface BillingModalProps {
  visible: boolean;
  config: BillingModalConfig;
  onClose: () => void;
}

export function BillingModal({ visible, config, onClose }: BillingModalProps) {
  const { height: windowHeight } = useWindowDimensions();
  const { icon, title, description, features, buttons, preventBackdropClose, testID } = config;
  const testIDPrefix = testID || 'billing-modal';

  // Animation values
  const backdropOpacity = useSharedValue(0);
  const cardOpacity = useSharedValue(0);
  const cardTranslateY = useSharedValue(40);
  const cardScale = useSharedValue(0.95);

  useEffect(() => {
    if (visible) {
      // Check reduced motion preference
      AccessibilityInfo.isReduceMotionEnabled().then((reduceMotion) => {
        if (reduceMotion) {
          backdropOpacity.value = 0.5;
          cardOpacity.value = 1;
          cardTranslateY.value = 0;
          cardScale.value = 1;
        } else {
          backdropOpacity.value = withTiming(0.5, { duration: 200 });
          cardOpacity.value = withTiming(1, { duration: 200 });
          // Convert tension/friction to stiffness/damping for reanimated
          const springConfig = { stiffness: ANIMATION.spring.tension, damping: ANIMATION.spring.friction };
          cardTranslateY.value = withSpring(0, springConfig);
          cardScale.value = withSpring(1, springConfig);
        }
      });
    }
  }, [visible, backdropOpacity, cardOpacity, cardTranslateY, cardScale]);

  const handleClose = () => {
    backdropOpacity.value = withTiming(0, { duration: ANIMATION.fast });
    cardOpacity.value = withTiming(0, { duration: ANIMATION.fast }, () => {
      runOnJS(onClose)();
    });
    cardTranslateY.value = withTiming(40, { duration: ANIMATION.fast });
    cardScale.value = withTiming(0.95, { duration: ANIMATION.fast });
  };

  const handleBackdropPress = () => {
    if (!preventBackdropClose) {
      handleClose();
    }
  };

  const handleButtonPress = (onPress: () => void) => {
    // Run onPress inside the Reanimated callback to avoid race condition:
    // setTimeout + handleClose's runOnJS(onClose) had non-deterministic order,
    // so if onClose fired after the next showBillingModal, the new config got nulled.
    // The onPress callback owns the transition (dismiss or show next step).
    backdropOpacity.value = withTiming(0, { duration: ANIMATION.fast });
    cardOpacity.value = withTiming(0, { duration: ANIMATION.fast }, () => {
      runOnJS(onPress)();
    });
    cardTranslateY.value = withTiming(40, { duration: ANIMATION.fast });
    cardScale.value = withTiming(0.95, { duration: ANIMATION.fast });
  };

  const backdropStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value,
  }));

  const cardAnimatedStyle = useAnimatedStyle(() => ({
    opacity: cardOpacity.value,
    transform: [
      { translateY: cardTranslateY.value },
      { scale: cardScale.value },
    ],
  }));

  if (!visible) return null;

  const renderIcon = () => {
    if (!icon) return null;
    if (typeof icon === 'string') {
      return <Text style={styles.icon}>{icon}</Text>;
    }
    return <View style={styles.iconContainer}>{icon}</View>;
  };

  return (
    <Modal transparent visible={visible} animationType="none" statusBarTranslucent>
      <View style={styles.overlay}>
        {/* Backdrop */}
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={handleBackdropPress}
          accessibilityRole="none"
          testID={`${testIDPrefix}-backdrop`}
        >
          <Animated.View style={[styles.backdrop, backdropStyle]} />
        </Pressable>

        {/* Modal Card */}
        <Animated.View style={[styles.cardWrapper, cardAnimatedStyle]}>
          <GlassCard
            style={Platform.OS === 'web' ? styles.cardWeb : { maxHeight: windowHeight * 0.85 }}
          >
            <View
              accessibilityRole="alert"
              accessibilityLabel={title}
              testID={`${testIDPrefix}-card`}
            >
              {/* Icon */}
              {renderIcon()}

              {/* Title */}
              <Text style={styles.title}>{title}</Text>

              {/* Description */}
              {description && (
                <Text style={styles.description}>{description}</Text>
              )}

              {/* Feature List */}
              {features && features.length > 0 && (
                <View style={styles.featureList}>
                  {features.map((feature, i) => (
                    <View key={i} style={styles.featureRow}>
                      {feature.icon && (
                        <Text style={styles.featureIcon}>{feature.icon}</Text>
                      )}
                      <Text
                        style={[
                          styles.featureText,
                          feature.emphasis && styles.featureEmphasis,
                        ]}
                      >
                        {feature.text}
                      </Text>
                    </View>
                  ))}
                </View>
              )}

              {/* Buttons */}
              <ScrollView
                style={{ maxHeight: windowHeight * 0.4 }}
                contentContainerStyle={styles.buttonContainer}
                bounces={false}
                showsVerticalScrollIndicator={false}
              >
                {buttons.map((button, index) => (
                  <BillingModalButtonComponent
                    key={index}
                    config={{
                      ...button,
                      onPress: () => handleButtonPress(button.onPress),
                    }}
                    index={index}
                    testIDPrefix={testIDPrefix}
                  />
                ))}
              </ScrollView>
            </View>
          </GlassCard>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backdrop: {
    position: 'absolute' as const, top: 0, right: 0, bottom: 0, left: 0,
    backgroundColor: '#000000',
  },
  cardWrapper: {
    maxWidth: 360,
    width: '90%',
  },
  cardWeb: {
    maxHeight: '85vh' as unknown as number,
  },
  icon: {
    fontSize: 32,
    textAlign: 'center',
    marginBottom: 8,
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: 8,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
  },
  description: {
    fontSize: 15,
    color: '#4B5563',
    lineHeight: 22,
    marginBottom: 16,
  },
  featureList: {
    marginBottom: 16,
    gap: 8,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  featureIcon: {
    fontSize: 16,
    width: 24,
  },
  featureText: {
    fontSize: 14,
    color: '#374151',
    flex: 1,
  },
  featureEmphasis: {
    fontWeight: '600',
  },
  buttonContainer: {
    gap: 10,
  },
});
